import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowRight, X, CheckCircle2, Circle } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const DISMISS_KEY = "tr_verified_promo_dismissed_session";

/**
 * VerifiedBadgePromo — friendly nudge card shown on the main dashboard when
 * the active political entity has a verification score < 85. Previews what
 * the "Verified Campaign" badge will look like on the donor microsite and
 * lists the exact remaining verification components with deep-links.
 *
 * Props:
 *   entity  { name, verification_summary: { score, status, components } }
 */
const COMPONENT_ROUTES = {
  official_email: "/verify-campaign",
  website_check: "/organizations",
  domain_match: "/verify-campaign",
  filing_match: "/organizations",
  manual_review: "/help",
};

export function VerifiedBadgePromo({ entity }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY)) setDismissed(true);
    } catch {
      /* no-op */
    }
  }, []);

  const summary = entity?.verification_summary;
  const shouldShow = !!(summary && summary.score < 85);

  useEffect(() => {
    if (shouldShow && !dismissed) {
      trackEvent("verified_badge_promo_shown", { score: summary?.score });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, dismissed]);

  if (!shouldShow || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    trackEvent("verified_badge_promo_dismiss");
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* no-op */
    }
  };

  const pct = Math.max(0, Math.min(100, Math.round((summary.score / 85) * 100)));
  const remaining = 85 - summary.score;
  const missing = (summary.components || []).filter((c) => !c.done);
  const firstMissing = missing[0];

  const goVerify = (component) => {
    trackEvent("verified_badge_promo_click", {
      component: component?.key,
      score: summary.score,
    });
    navigate(component ? COMPONENT_ROUTES[component.key] || "/verify-campaign" : "/verify-campaign");
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/[0.06] via-orange-500/[0.04] to-transparent p-6"
      data-testid="verified-badge-promo"
    >
      <button
        onClick={dismiss}
        data-testid="verified-badge-promo-dismiss-btn"
        className="absolute top-3 right-3 text-slate-500 hover:text-white"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-center">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
            Unlock donor trust
          </div>
          <h3 className="font-display text-2xl font-bold text-white mt-1">
            Get the Verified Campaign badge on your donation pages
          </h3>
          <p className="text-sm text-slate-300 mt-2 max-w-xl">
            Verified campaigns raise more per visitor — the badge sits next to your candidate
            name on every microsite and signals authenticity to donors before they enter a
            card.
          </p>

          {/* Progress bar */}
          <div className="mt-5">
            <div className="flex items-baseline justify-between text-xs text-slate-400">
              <span data-testid="verified-badge-promo-score">
                Verification score{" "}
                <b className="text-white">
                  {summary.score}
                </b>
                <span className="text-slate-500"> / 85</span>
              </span>
              <span className="text-amber-300 font-medium">
                {remaining > 0 ? `${remaining} points to go` : "Ready to verify"}
              </span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all"
                style={{ width: `${pct}%` }}
                data-testid="verified-badge-promo-progress"
              />
            </div>
          </div>

          {/* Missing components list */}
          <div className="mt-4 flex flex-wrap gap-1.5" data-testid="verified-badge-promo-missing">
            {(summary.components || []).map((c) => (
              <button
                key={c.key}
                onClick={() => !c.done && goVerify(c)}
                data-testid={`verified-badge-promo-component-${c.key}`}
                className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border transition-colors ${
                  c.done
                    ? "border-emerald-400/40 bg-emerald-400/5 text-emerald-300 cursor-default"
                    : "border-amber-400/30 text-amber-200 hover:border-amber-400/60 hover:bg-amber-400/10"
                }`}
              >
                {c.done ? (
                  <CheckCircle2 className="w-2.5 h-2.5" />
                ) : (
                  <Circle className="w-2.5 h-2.5" />
                )}
                {c.label} · +{c.points}
              </button>
            ))}
          </div>

          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => goVerify(firstMissing)}
              data-testid="verified-badge-promo-cta"
              className="rounded-full px-5 py-2.5 text-sm bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-semibold inline-flex items-center gap-1.5"
            >
              <ShieldCheck className="w-4 h-4" />
              {firstMissing ? `Complete ${firstMissing.label}` : "Verify campaign"}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/onboarding/checklist")}
              className="text-xs text-amber-300 hover:text-amber-100 px-3 py-2 inline-flex items-center gap-1"
              data-testid="verified-badge-promo-checklist-btn"
            >
              See full checklist <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Microsite preview mock */}
        <div className="w-full lg:w-72 shrink-0" data-testid="verified-badge-promo-preview">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            Preview on your donation page
          </div>
          <div className="rounded-xl border border-white/10 bg-slate-950/60 p-4 relative overflow-hidden">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              totalreach.us/m/
              {(entity?.slug || (entity?.name || "campaign").toLowerCase().replace(/\s+/g, "-")).slice(0, 22)}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm text-white font-semibold truncate">
                  {entity?.name || "Your Campaign"}
                </div>
                <div className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 text-emerald-300">
                  <ShieldCheck className="w-2.5 h-2.5" /> Verified Campaign
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-1.5">
              {[25, 50, 100].map((v) => (
                <div
                  key={v}
                  className="text-center text-[11px] py-1.5 rounded-md border border-white/10 bg-white/[0.02] text-slate-300"
                >
                  ${v}
                </div>
              ))}
            </div>
            <div className="mt-1.5 h-6 rounded-md bg-gradient-to-r from-cyan-400 to-blue-500 opacity-90" />
            <div className="mt-3 text-[9px] text-slate-500 leading-tight">
              Donors see the badge before they enter a card — verified campaigns raise more per
              visitor.
            </div>
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-slate-950/70" />
          </div>
        </div>
      </div>
    </div>
  );
}
